import { useEffect, useState } from "react";
import { Receipt, FileText, Settings2, BarChart3, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { BillableTab } from "@/components/finance/BillableTab";
import { InvoicesTab } from "@/components/finance/InvoicesTab";
import { ReminderSettings } from "@/components/finance/ReminderSettings";
import { ReminderTemplates } from "@/components/finance/ReminderTemplates";
import { SignatureSettings } from "@/components/finance/SignatureSettings";
import { ArReportTab } from "@/components/finance/ArReportTab";
import { WhatsAppConnection } from "@/components/finance/WhatsAppConnection";
import { ArWeeklySettings } from "@/components/finance/ArWeeklySettings";
import { CreditApprovals } from "@/components/finance/CreditApprovals";
import { BankAccountsSettings, PrinterPanel } from "@/components/finance/BankAccountsSettings";
import { BUCKET_LABEL, bucketCls } from "@/components/finance/aging";

const CARDS = ["current", "1-30", "31-60", "61-90", "90+"];

function AgingCards({ summary, bucket, onPick }) {
  return (
    <div className="aging-grid" data-testid="ar-aging-summary">
      <div className="aging-card total" data-testid="ar-outstanding">
        <span>Total Piutang (AR)</span>
        <strong>{rupiah(summary?.outstanding || 0)}</strong>
        <small>Lewat jatuh tempo {rupiah(summary?.overdue_amount || 0)}</small>
      </div>
      {CARDS.map((b) => {
        const v = summary?.buckets?.[b] || { amount: 0, count: 0 };
        return (
          <button key={b} className={`aging-card ${bucketCls(b)} ${bucket === b ? "active" : ""}`}
            onClick={() => onPick(bucket === b ? "all" : b)} data-testid={`aging-card-${bucketCls(b)}`}>
            <span>{BUCKET_LABEL[b]}</span><strong>{rupiah(v.amount)}</strong><small>{v.count} invoice</small>
          </button>
        );
      })}
    </div>
  );
}

function SettingsTab({ hasPerm }) {
  const [waKey, setWaKey] = useState(0);
  const [bankKey, setBankKey] = useState(0);
  return (
    <div className="fin-settings-stack">
      {hasPerm("whatsapp:connection") && <WhatsAppConnection onChanged={() => setWaKey((k) => k + 1)} />}
      {hasPerm("invoices:create") && <ReminderSettings key={waKey} />}
      <ReminderTemplates />
      <BankAccountsSettings onChanged={() => setBankKey((k) => k + 1)} />
      <SignatureSettings key={bankKey} />
      <PrinterPanel />
      <ArWeeklySettings />
    </div>
  );
}

export default function Finance() {
  const { hasPerm } = useAuth();
  const canCredit = hasPerm("credit:approve");
  const [tab, setTab] = useState(canCredit ? "credit" : "billable");
  const [summary, setSummary] = useState(null);
  const [credits, setCredits] = useState(null);
  const [bucket, setBucket] = useState("all");
  const [key, setKey] = useState(0);
  const reload = () => setKey((k) => k + 1);

  useEffect(() => {
    api.get("/finance/summary").then((r) => setSummary(r.data.data)).catch((e) => toast.error(apiError(e)));
    if (canCredit) api.get("/credit-approvals").then((r) => setCredits(r.data.meta.count)).catch(() => {});
  }, [key, canCredit]);

  useEffect(() => { if (canCredit && credits === 0 && tab === "credit") setTab("billable"); }, [credits]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (b) => { setBucket(b); setTab("invoices"); };
  const tabs = [
    ...(canCredit ? [{ k: "credit", label: `Persetujuan Kredit (${credits ?? "…"})`, icon: Wallet, alert: credits > 0 }] : []),
    { k: "billable", label: `Siap Ditagih (${summary?.billable_count ?? "…"})`, icon: Receipt },
    { k: "invoices", label: "Invoice & Pembayaran", icon: FileText },
    { k: "ar-report", label: "Laporan AR Customer", icon: BarChart3 },
    ...(hasPerm("invoices:create") || hasPerm("whatsapp:connection") ? [{ k: "settings", label: "Pengaturan", icon: Settings2 }] : []),
  ];

  return (
    <div data-testid="finance-page">
      <div className="section-head compact">
        <div>
          <p className="eyebrow">KEUANGAN</p>
          <h2>Finance & Penagihan</h2>
          <p>Setujui order yang melewati batas kredit, tagih order yang sudah dikirim, verifikasi pembayaran, dan pantau umur piutang (AR Aging).</p>
        </div>
      </div>
      <AgingCards summary={summary} bucket={bucket} onPick={pick} />
      <div className="fin-tabs" data-testid="finance-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} className={`${tab === t.k ? "active" : ""} ${t.alert ? "alert" : ""}`} onClick={() => setTab(t.k)} data-testid={`finance-tab-${t.k}`}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "credit" && <CreditApprovals refreshKey={key} onChanged={reload} />}
      {tab === "billable" && <BillableTab refreshKey={key} onBilled={reload} />}
      {tab === "invoices" && <InvoicesTab refreshKey={key} bucket={bucket} setBucket={setBucket} onChanged={reload} />}
      {tab === "ar-report" && <ArReportTab refreshKey={key} />}
      {tab === "settings" && <SettingsTab hasPerm={hasPerm} />}
    </div>
  );
}
